import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type CalEvent, type EventBlock } from './useGoogleCalendarWeek';
import { specialistName } from '../lib/specialist-name';

// Denser than the GCal preview so a full working day fits without vertical
// scrolling on desktop.
const APPT_HOUR_PX = 30;

/** Week-view geometry for appointments — the same layout the Google Calendar
 *  preview uses (see useGoogleCalendarWeek), but fed from our Appointment rows
 *  instead of a GCal fetch. Returns a `CalendarWeek`-compatible shape so the
 *  presentational CalendarWeekGrid can render it unchanged. */

const DEFAULT_START_HOUR = 6;
const DEFAULT_END_HOUR = 20;

// Per-doctor tint (border + bg). Colours are assigned to specialists in order of
// first appearance, cycling through the palette. text stays `text-foreground`.
const PALETTE = [
  'border-sky-500/50 bg-sky-500/15',
  'border-emerald-500/50 bg-emerald-500/15',
  'border-amber-500/50 bg-amber-500/15',
  'border-violet-500/50 bg-violet-500/15',
  'border-rose-500/50 bg-rose-500/15',
  'border-cyan-500/50 bg-cyan-500/15',
  'border-fuchsia-500/50 bg-fuchsia-500/15',
  'border-lime-600/50 bg-lime-600/15',
];

type ApptLite = {
  id: string;
  startsAt: string | Date;
  endsAt: string | Date;
  patientName?: string | null;
  specialistId?: string | null;
  specialist?: {
    displayName?: unknown;
    user?: { name?: string | null; email?: string | null } | null;
  } | null;
};

/** Monday 00:00 (local) of the week `offsetWeeks` away from today. */
function startOfWeek(offsetWeeks: number): Date {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const mondayIndex = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - mondayIndex + offsetWeeks * 7);
  return d;
}

/** Side-by-side layout for overlapping events (Google-Calendar style). Blocks
 *  that overlap in time are grouped into a cluster and split into columns:
 *  each block gets a `lane` index and its cluster's `laneCount`, which the grid
 *  turns into left/width. Non-overlapping blocks stay `laneCount: 1` (full
 *  width). Overlap is measured on the rendered top/height (pixels ∝ time). */
function packLanes(blocks: EventBlock[]): EventBlock[] {
  if (blocks.length < 2) {
    if (blocks[0]) blocks[0].laneCount = 1;
    return blocks;
  }
  const bottom = (b: EventBlock) => b.top + b.height;
  const sorted = [...blocks].sort((a, b) => a.top - b.top || bottom(a) - bottom(b));
  let i = 0;
  while (i < sorted.length) {
    // Gather one cluster of transitively-overlapping blocks.
    let clusterEnd = bottom(sorted[i]!);
    let j = i + 1;
    while (j < sorted.length && sorted[j]!.top < clusterEnd) {
      clusterEnd = Math.max(clusterEnd, bottom(sorted[j]!));
      j++;
    }
    // Greedy column assignment: reuse the first column that's already free.
    const laneEnds: number[] = [];
    for (let k = i; k < j; k++) {
      const b = sorted[k]!;
      let lane = laneEnds.findIndex((end) => end <= b.top);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(bottom(b));
      } else {
        laneEnds[lane] = bottom(b);
      }
      b.lane = lane;
    }
    for (let k = i; k < j; k++) sorted[k]!.laneCount = laneEnds.length;
    i = j;
  }
  return sorted;
}

export function useAppointmentsWeek(appointments: ApptLite[]) {
  const { i18n } = useTranslation();
  const [weekOffset, setWeekOffset] = useState(0);
  const weekStart = useMemo(() => startOfWeek(weekOffset), [weekOffset]);

  const events: CalEvent[] = useMemo(
    () =>
      appointments.map((a) => ({
        id: a.id,
        summary: a.patientName || '—',
        description: a.specialist ? specialistName(a.specialist, i18n.language) : null,
        location: null,
        start: new Date(a.startsAt),
        end: new Date(a.endsAt),
        allDay: false,
        status: null,
        htmlLink: null,
      })),
    [appointments, i18n.language],
  );

  // Assign each specialist a colour (first-seen order), and a per-appointment
  // lookup + a legend of {doctor, colour}.
  const { colorByEventId, legend } = useMemo(() => {
    const byId = new Map<string, string>();
    const seen = new Map<string, string>();
    const leg: { label: string; color: string }[] = [];
    let idx = 0;
    for (const a of appointments) {
      const key = a.specialistId || a.specialist?.user?.email || 'unknown';
      let color = seen.get(key);
      if (!color) {
        color = PALETTE[idx % PALETTE.length]!;
        idx += 1;
        seen.set(key, color);
        leg.push({
          label: a.specialist ? specialistName(a.specialist, i18n.language) : '—',
          color,
        });
      }
      byId.set(a.id, color);
    }
    return { colorByEventId: byId, legend: leg };
  }, [appointments, i18n.language]);

  const days = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const d = new Date(weekStart);
        d.setDate(d.getDate() + i);
        return d;
      }),
    [weekStart],
  );

  const [startHour, endHour] = useMemo(() => {
    let lo = DEFAULT_START_HOUR;
    let hi = DEFAULT_END_HOUR;
    for (const e of events) {
      lo = Math.min(lo, e.start.getHours());
      hi = Math.max(hi, e.end.getHours() + (e.end.getMinutes() > 0 ? 1 : 0));
    }
    return [Math.max(0, lo), Math.min(24, Math.max(hi, lo + 1))];
  }, [events]);

  const hours = useMemo(
    () => Array.from({ length: endHour - startHour + 1 }, (_, i) => startHour + i),
    [startHour, endHour],
  );
  const gridHeight = (endHour - startHour) * APPT_HOUR_PX;

  const timeFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit', hour12: false }),
    [i18n.language],
  );
  const dayFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { weekday: 'short', day: 'numeric' }),
    [i18n.language],
  );
  const rangeFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short' }),
    [i18n.language],
  );
  const listDayFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { weekday: 'long', day: 'numeric', month: 'short' }),
    [i18n.language],
  );

  const title = (e: CalEvent) => e.summary || '—';

  function blocksForDay(day: Date): EventBlock[] {
    const dayTop = new Date(day);
    dayTop.setHours(startHour, 0, 0, 0);
    const dayBottom = new Date(day);
    dayBottom.setHours(0, 0, 0, 0);
    dayBottom.setHours(endHour, 0, 0, 0);
    const blocks = events.flatMap((e) => {
      const s = Math.max(e.start.getTime(), dayTop.getTime());
      const en = Math.min(e.end.getTime(), dayBottom.getTime());
      if (en <= s) return [];
      const top = ((s - dayTop.getTime()) / 3_600_000) * APPT_HOUR_PX;
      const height = ((en - s) / 3_600_000) * APPT_HOUR_PX;
      const doctor = e.description ? ` · ${e.description}` : '';
      return [
        {
          key: `${e.id}-${day.getDate()}`,
          top,
          height: Math.max(height, 14),
          title: title(e),
          tip: `${timeFmt.format(e.start)}–${timeFmt.format(e.end)} ${title(e)}${doctor}`,
          color: colorByEventId.get(e.id),
        },
      ];
    });
    return packLanes(blocks);
  }

  return {
    weekOffset,
    setWeekOffset,
    days,
    events,
    allDayEvents: [] as CalEvent[],
    startHour,
    hours,
    gridHeight,
    timeFmt,
    dayFmt,
    rangeFmt,
    listDayFmt,
    title,
    blocksForDay,
    legend,
    hourPx: APPT_HOUR_PX,
    isLoading: false,
    isFetching: false,
    refetch: () => {},
    errParts: null,
  };
}
